import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Brand } from "@/components/Brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { Wallet, Loader2, ArrowLeft, Eye, EyeOff, IndianRupee } from "lucide-react";

export default function UserLogin() {
  const nav = useNavigate();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password, "user");
      toast.success("Signed in successfully");
      nav("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen vp-grid-bg grid lg:grid-cols-2">
      <div className="flex items-center justify-center p-6 order-2 lg:order-1">
        <motion.div initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="w-full max-w-md">
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-400 hover:text-white mb-8"><ArrowLeft className="h-4 w-4" /> Back home</Link>
          <div className="lg:hidden mb-6"><Brand /></div>
          <h1 className="font-display text-3xl font-extrabold">Welcome back</h1>
          <p className="mt-2 text-slate-400">Sign in to your VantorPay wallet.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="email" className="text-slate-300">Email</Label>
              <Input id="email" data-testid="user-email-input" type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com" className="mt-1.5 bg-slate-900/60 border-slate-700 h-11" />
            </div>
            <div>
              <Label htmlFor="password" className="text-slate-300">Password</Label>
              <div className="relative mt-1.5">
                <Input id="password" data-testid="user-password-input" type={show ? "text" : "password"} required value={password}
                  onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="bg-slate-900/60 border-slate-700 h-11 pr-11" />
                <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white" data-testid="toggle-password">
                  {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <Button type="submit" disabled={loading} data-testid="user-login-submit"
              className="w-full h-11 bg-emerald-500 hover:bg-emerald-400 text-[#08120e] font-semibold text-base">
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
            </Button>
          </form>
          <p className="mt-6 text-sm text-slate-500">Accounts are created by your admin. No public sign-up.</p>
          <p className="mt-2 text-sm text-slate-500">Admin? <Link to="/admin/login" className="text-emerald-400 hover:underline">Admin login</Link></p>
        </motion.div>
      </div>

      <div className="hidden lg:flex flex-col justify-between p-10 border-l border-white/5 bg-black/20 order-1 lg:order-2">
        <div className="flex justify-end"><Brand /></div>
        <div className="max-w-md ml-auto text-right">
          <div className="h-12 w-12 grid place-items-center rounded-xl bg-cyan-500/10 text-cyan-400 ml-auto"><Wallet className="h-6 w-6" /></div>
          <h2 className="mt-6 font-display text-3xl font-bold">Your money, in view</h2>
          <p className="mt-3 text-slate-400 leading-relaxed">Track balances, submit deposits with your assigned UPI/QR, request payouts and follow every UTR in real time.</p>
        </div>
        <p className="text-xs text-slate-600 font-mono flex items-center justify-end gap-1"><IndianRupee className="h-3 w-3" /> INR · IST SYNCED WALLET</p>
      </div>
    </div>
  );
}
